import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowRight, ClipboardList, Users, Loader2, Home } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public guest-friendly page at /join — anyone with a 6-char game code
 * can hop into a live stats game and start tracking, no login required.
 *
 * This page is intentionally hidden (not linked from nav) and meant to be
 * shared by the game owner with their stat-keepers.
 */
export default function JoinGamePage() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [joining, setJoining] = useState(false);

  const handleJoin = async (e) => {
    if (e) e.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) {
      toast.error('Enter the 6-character game code');
      return;
    }
    setJoining(true);
    try {
      const res = await fetch(`${API_URL}/api/stats/games/join/${trimmed}`, {
        method: 'POST',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || `Couldn't find an active game with code ${trimmed}`);
        return;
      }
      toast.success(`Joining ${data.game?.name || 'game'}…`);
      navigate(`/stats/game/${data.game.id}`);
    } catch {
      toast.error('Network error — check your connection and try again');
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100 flex items-center justify-center p-4">
      {/* Home button — pinned to the top-left so guests who landed here by
          mistake (or want to explore the product first) can bail to the
          landing page without hunting for a link. */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/')}
        className="absolute top-4 left-4 text-slate-600 hover:text-[#00247D]"
        data-testid="join-home-btn"
      >
        <Home className="w-4 h-4 mr-1.5" />Home
      </Button>
      <Card className="w-full max-w-md border-2 border-[#00247D]/10 shadow-xl">
        <CardContent className="p-8">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-[#00247D] text-white flex items-center justify-center shadow-md">
              <ClipboardList className="w-8 h-8" />
            </div>
          </div>
          <h1
            className="text-3xl text-center font-bold text-[#00247D] mb-2 tracking-wide"
            style={{ fontFamily: 'Barlow Condensed' }}
            data-testid="join-page-title"
          >
            JOIN A GAME
          </h1>
          <p className="text-center text-sm text-slate-500 mb-6">
            Enter the 6-character code from your tournament organizer to start tracking stats. No account needed.
          </p>

          <form onSubmit={handleJoin} className="space-y-4" data-testid="join-game-form">
            <div>
              <label className="text-xs font-semibold tracking-wide text-slate-600 block mb-1.5">GAME CODE</label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="A1B2C3"
                maxLength={6}
                autoFocus
                className="text-center text-3xl font-mono tracking-[0.4em] uppercase h-16 border-2 border-slate-200 focus:border-[#00247D]"
                data-testid="join-code-input"
              />
            </div>

            <Button
              type="submit"
              disabled={joining || code.trim().length === 0}
              className="w-full h-12 bg-[#00247D] hover:bg-[#001A5C] text-white text-base font-semibold disabled:opacity-50"
              data-testid="join-submit-btn"
            >
              {joining ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Joining…</>
              ) : (
                <>Start Tracking <ArrowRight className="w-5 h-5 ml-2" /></>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-100 flex items-start gap-3 text-xs text-slate-500">
            <Users className="w-4 h-4 shrink-0 mt-0.5 text-[#00247D]/60" />
            <p>
              Multiple stat-keepers can join the same game with the same code — perfect for splitting team A and team B between two devices.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
